import React, { useRef, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'

import maldivesOption1 from '../../../assets/assets-maldives/options/1.jpg'
import maldivesOption2 from '../../../assets/assets-maldives/options/2.jpg'
import maldivesOption3 from '../../../assets/assets-maldives/options/3.jpg'
import maldivesOption4 from '../../../assets/assets-maldives/options/4.jpg'
import maldivesOption5 from '../../../assets/assets-maldives/options/5.jpg'

type SlideData = {
    image: string
    key: string
}

const slides: SlideData[] = [
    { image: maldivesOption1, key: 'family' },
    { image: maldivesOption2, key: 'honeymoons' },
    { image: maldivesOption3, key: 'weddings' },
    { image: maldivesOption4, key: 'wellness' },
    { image: maldivesOption5, key: 'ultraPrivate' },
]

const YourKindOfMaldivesSlider = () => {
    const { t, i18n } = useTranslation('maldives');
    const isRu = i18n.language === 'ru';
    const swiperRef = useRef<any>(null)

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!swiperRef.current) return
            const swiper = swiperRef.current.swiper
            const clickedSlide = (e.target as HTMLElement).closest('.swiper-slide')

            if (!clickedSlide) return

            if (clickedSlide.classList.contains('swiper-slide-next')) {
                swiper.slideNext()
            } else if (clickedSlide.classList.contains('swiper-slide-prev')) {
                swiper.slidePrev()
            }
        }

        const sliderElement = swiperRef.current
        if (sliderElement) {
            sliderElement.addEventListener('click', handleClick)
        }

        return () => {
            if (sliderElement) {
                sliderElement.removeEventListener('click', handleClick)
            }
        }
    }, [])

    return (
        <section className="lg:py-[60px] sm:py-10 py-8 bg-white">
            <div className={`max-w-[520px] lg:max-w-[1600px] mx-auto text-center mb-6 sm:mb-8 lg:mb-10 px-5 sm:px-6 lg:px-10`}>
                <h2 className="section-heading">
                    {t('yourKind.heading')}
                </h2>

                <p className="text-xs sm:text-lg text-[#4A4A4A] font-normal mt-2 sm:mt-2.5 lg:mt-4">
                    <Trans
                        i18nKey="yourKind.subheading"
                        ns="maldives"
                        components={{ br1: <br className='sm:hidden' /> }}
                    />
                </p>
            </div>

            <Swiper
                ref={swiperRef}
                modules={[Pagination]}
                slidesPerView="auto"
                spaceBetween={8}
                slidesOffsetBefore={20}
                slidesOffsetAfter={20}
                rewind={true}
                initialSlide={0}
                breakpoints={{
                    640: { slidesOffsetBefore: 24, slidesOffsetAfter: 24 },
                    768: { spaceBetween: 20 },
                    1024: { spaceBetween: 24, slidesOffsetBefore: 40, slidesOffsetAfter: 40 }
                }}
                pagination={{
                    clickable: true,
                    el: '.maldives-pagination'
                }}
            >
                {slides.map((slide) => {
                    const badge = t(`yourKind.slides.${slide.key}.badge`)
                    const heading = t(`yourKind.slides.${slide.key}.heading`)
                    const description = t(`yourKind.slides.${slide.key}.description`)
                    return (
                        <SwiperSlide key={slide.key} className={`lg:!w-[688px] sm:!w-[504px] !w-[300px]`}>
                            <div className={`lg:min-h-[350px] sm:min-h-[256px] ${isRu ? 'min-h-[200px]' : 'min-h-[180px]'} rounded-xl lg:rounded-[20px] lg:p-[27px] sm:p-5 p-3 relative overflow-hidden flex flex-col w-full`}>
                                <img src={slide.image} alt={heading} className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                                <div className="relative h-full flex flex-col justify-between flex-1">
                                    <div>
                                        {badge && (
                                            <span className="inline-flex items-center h-5 sm:h-8 lg:h-[37px] px-2.5 text-white text-[10px] sm:text-xs lg:text-sm rounded-[4px] sm:rounded-lg lg:rounded-[10px] bg-[#7AA1C4]">
                                                {badge}
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className={`font-semibold text-white leading-tight lg:text-[32px] sm:text-2xl text-sm`}>
                                            {heading}
                                        </h3>

                                        <p className={`lg:text-lg sm:text-sm text-xs text-white mt-1 lg:mt-4`}>
                                            {description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    )
                })}
            </Swiper>

            <div className="pagination maldives-pagination" />
        </section>
    )
}

export default YourKindOfMaldivesSlider
